import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface FeatureFlag {
  id: string;
  key: string;
  enabled: boolean;
  description: string | null;
  updated_at: string;
}

interface FeatureFlagsContextType {
  flags: Record<string, boolean>;
  flagDetails: FeatureFlag[];
  loading: boolean;
  isEnabled: (key: string) => boolean;
  refreshFlags: () => Promise<void>;
  updateFlag: (key: string, enabled: boolean) => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

export const FeatureFlagsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [flagDetails, setFlagDetails] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchFlags = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('feature_flags')
        .select('*')
        .order('key');

      if (error) {
        console.error('Error fetching feature flags:', error);
        return;
      }

      const flagsMap: Record<string, boolean> = {};
      (data || []).forEach((flag) => {
        flagsMap[flag.key] = flag.enabled;
      });

      setFlags(flagsMap);
      setFlagDetails(data || []);
    } catch (error) {
      console.error('Error in fetchFlags:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshFlags = useCallback(async () => {
    setLoading(true);
    await fetchFlags();
  }, [fetchFlags]);

  const updateFlag = useCallback(async (key: string, enabled: boolean) => {
    const { error } = await supabase
      .from('feature_flags')
      .update({ enabled })
      .eq('key', key);

    if (error) {
      console.error('Error updating feature flag:', error);
      throw error;
    }

    // Update local state immediately
    setFlags(prev => ({ ...prev, [key]: enabled }));
    setFlagDetails(prev => 
      prev.map(flag => 
        flag.key === key ? { ...flag, enabled } : flag
      )
    );
  }, []);

  const isEnabled = useCallback((key: string): boolean => {
    return flags[key] ?? false;
  }, [flags]);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  return (
    <FeatureFlagsContext.Provider value={{ 
      flags, 
      flagDetails, 
      loading, 
      isEnabled, 
      refreshFlags,
      updateFlag 
    }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = (): FeatureFlagsContextType => {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
};

export const useFeatureFlag = (key: string): boolean => {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(key);
};
